import { useState, useCallback } from 'react';
import {
  clearAdminSession,
  readStoredSessionToken,
  shouldRefreshSessionToken,
  storeAdminSession,
  isSessionTokenExpired,
} from '../utils/adminSession';

let pendingRefreshPromise = null;

const useAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const verifyAndRefreshToken = useCallback(async (token) => {
    const activeToken = token || readStoredSessionToken();
    if (!activeToken) {
      setError('No token provided');
      return false;
    }

    setError(null);

    if (isSessionTokenExpired(activeToken)) {
      clearAdminSession();
      setError('Session expired');
      return false;
    }

    storeAdminSession({ token: activeToken });
    if (!shouldRefreshSessionToken(activeToken)) {
      return true;
    }

    setIsLoading(true);

    try {
      if (!pendingRefreshPromise) {
        pendingRefreshPromise = (async () => {
          const refreshResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}refreshtoken`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${activeToken}`,
            },
            body: JSON.stringify({ refresh_token: activeToken }),
          });

          const refreshData = await refreshResponse.json().catch(() => ({}));
          if (!refreshResponse.ok) {
            const refreshError = new Error(refreshData.message || 'Token refresh failed');
            refreshError.status = refreshResponse.status;
            throw refreshError;
          }

          if (!refreshData.token) {
            throw new Error('No token received from refresh endpoint');
          }

          storeAdminSession({
            token: refreshData.token,
            user: refreshData.user,
          });

          return true;
        })().finally(() => {
          pendingRefreshPromise = null;
        });
      }

      return await pendingRefreshPromise;
    } catch (err) {
      setError(err.message);

      if (err?.status === 401 || err?.status === 403) {
        clearAdminSession();
        return false;
      }

      if (!isSessionTokenExpired(activeToken)) {
        return true;
      }

      clearAdminSession();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { 
    verifyAndRefreshToken, 
    isLoading, 
    error,
    clearError 
  };
};

export default useAuth;
